/**
 * Cache management utility with cache-busting and performance tracking
 */

class CacheManager {
  constructor() {
    this.cache = new Map();
    this.cacheMetadata = new Map();
    
    // Configuration
    this.config = {
      defaultTtl: 5 * 60 * 1000, // 5 minutes default TTL
      maxCacheSize: 100, // Maximum number of cached items
      cleanupInterval: 2 * 60 * 1000, // Cleanup every 2 minutes
      compressionThreshold: 1024 // Compress items larger than 1KB
    };
    
    // Cache statistics
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      evictions: 0,
      compressions: 0
    };
    
    // Start periodic cleanup
    this.startCleanup();
  }

  /**
   * Generate a cache key with cache-busting parameters
   * @param {string} endpoint - API endpoint
   * @param {object} params - Request parameters
   * @param {object} options - Cache options
   * @returns {string} Cache key
   */
  generateCacheKey(endpoint, params = {}, options = {}) {
    const {
      userId,
      version,
      bustCache = false,
      cacheKeyPrefix = ''
    } = options;

    // Base key components
    const keyComponents = [cacheKeyPrefix, endpoint];
    
    // Add user ID for user-specific caching
    if (userId) {
      keyComponents.push(`user:${userId}`);
    }
    
    // Add version for cache versioning
    if (version) {
      keyComponents.push(`v:${version}`);
    }
    
    // Add sorted parameters
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}=${JSON.stringify(params[key])}`)
      .join('&');
    
    if (sortedParams) {
      keyComponents.push(sortedParams);
    }
    
    // Add cache-busting timestamp if requested
    if (bustCache) {
      keyComponents.push(`_t=${Date.now()}`);
    }
    
    return keyComponents.join('|');
  }

  /**
   * Get item from cache
   * @param {string} key - Cache key
   * @returns {any|null} Cached item or null if not found/expired
   */
  get(key) {
    const item = this.cache.get(key);
    const metadata = this.cacheMetadata.get(key);
    
    if (!item || !metadata) {
      this.stats.misses++;
      return null;
    }
    
    // Check if item has expired
    if (Date.now() > metadata.expiresAt) {
      this.delete(key);
      this.stats.misses++;
      return null;
    }
    
    // Update access statistics
    metadata.accessCount++;
    metadata.lastAccessedAt = Date.now();
    this.stats.hits++;
    
    // Decompress if needed
    let value = item.value;
    if (item.compressed) {
      try {
        value = JSON.parse(value);
      } catch (error) {
        console.error('[CacheManager] Failed to decompress cached item:', error);
        this.delete(key);
        this.stats.misses++;
        return null;
      }
    }
    
    console.log(`[CacheManager] Cache hit for key: ${key}`);
    return value;
  }

  /**
   * Set item in cache
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {object} options - Cache options
   */
  set(key, value, options = {}) {
    const {
      ttl = this.config.defaultTtl,
      tags = [],
      priority = 'normal'
    } = options;

    // Check cache size and evict if necessary
    if (this.cache.size >= this.config.maxCacheSize) {
      this.evictLeastRecentlyUsed();
    }

    // Prepare item for storage
    let itemToStore = { value, compressed: false };
    const serializedSize = JSON.stringify(value).length;
    
    // Compress large items
    if (serializedSize > this.config.compressionThreshold) {
      try {
        itemToStore = {
          value: JSON.stringify(value),
          compressed: true
        };
        this.stats.compressions++;
      } catch (error) {
        console.warn('[CacheManager] Failed to compress item, storing uncompressed:', error);
      }
    }

    // Create metadata
    const metadata = {
      key,
      createdAt: Date.now(),
      expiresAt: Date.now() + ttl,
      lastAccessedAt: Date.now(),
      accessCount: 0,
      size: serializedSize,
      tags,
      priority,
      ttl
    };

    // Store item and metadata
    this.cache.set(key, itemToStore);
    this.cacheMetadata.set(key, metadata);
    this.stats.sets++;

    console.log(`[CacheManager] Cached item: ${key} (${serializedSize} bytes, TTL: ${ttl}ms)`);
  }

  /**
   * Delete item from cache
   * @param {string} key - Cache key
   * @returns {boolean} True if item was deleted
   */
  delete(key) {
    const deleted = this.cache.delete(key) && this.cacheMetadata.delete(key);
    if (deleted) {
      console.log(`[CacheManager] Deleted cached item: ${key}`);
    }
    return deleted;
  }

  /**
   * Check if item exists in cache and is not expired
   * @param {string} key - Cache key
   * @returns {boolean} True if item exists and is valid
   */
  has(key) {
    const metadata = this.cacheMetadata.get(key);
    if (!metadata) return false;
    
    if (Date.now() > metadata.expiresAt) {
      this.delete(key);
      return false;
    }
    
    return this.cache.has(key);
  }

  /**
   * Clear all cached items
   */
  clear() {
    const size = this.cache.size;
    this.cache.clear();
    this.cacheMetadata.clear();
    console.log(`[CacheManager] Cleared ${size} cached items`);
  }

  /**
   * Clear cached items by tag
   * @param {string} tag - Tag to clear
   * @returns {number} Number of items cleared
   */
  clearByTag(tag) {
    let clearedCount = 0;
    
    for (const [key, metadata] of this.cacheMetadata.entries()) {
      if (metadata.tags.includes(tag)) {
        this.delete(key);
        clearedCount++;
      }
    }
    
    console.log(`[CacheManager] Cleared ${clearedCount} items with tag: ${tag}`);
    return clearedCount;
  }

  /**
   * Clear cached items by key pattern
   * @param {RegExp|string} pattern - Pattern to match keys
   * @returns {number} Number of items cleared
   */
  clearByPattern(pattern) {
    let clearedCount = 0;
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.delete(key);
        clearedCount++;
      }
    }
    
    console.log(`[CacheManager] Cleared ${clearedCount} items matching pattern: ${pattern}`);
    return clearedCount;
  }

  /**
   * Evict least recently used items
   * @param {number} count - Number of items to evict (default: 10% of max size)
   */
  evictLeastRecentlyUsed(count = Math.ceil(this.config.maxCacheSize * 0.1)) {
    // Get items sorted by last access time (oldest first)
    const itemsByAccess = Array.from(this.cacheMetadata.entries())
      .sort(([, a], [, b]) => a.lastAccessedAt - b.lastAccessedAt)
      .slice(0, count);

    let evictedCount = 0;
    for (const [key] of itemsByAccess) {
      this.delete(key);
      evictedCount++;
    }

    this.stats.evictions += evictedCount;
    console.log(`[CacheManager] Evicted ${evictedCount} least recently used items`);
  }

  /**
   * Get cache statistics
   * @returns {object} Cache statistics
   */
  getStatistics() {
    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? (this.stats.hits / totalRequests) * 100 : 0;
    
    // Calculate cache size information
    let totalSize = 0;
    let compressedItems = 0;
    
    for (const metadata of this.cacheMetadata.values()) {
      totalSize += metadata.size;
    }
    
    for (const item of this.cache.values()) {
      if (item.compressed) compressedItems++;
    }

    return {
      size: this.cache.size,
      maxSize: this.config.maxCacheSize,
      totalSize,
      averageItemSize: this.cache.size > 0 ? totalSize / this.cache.size : 0,
      compressedItems,
      compressionRate: this.cache.size > 0 ? (compressedItems / this.cache.size) * 100 : 0,
      hitRate,
      missRate: 100 - hitRate,
      hits: this.stats.hits,
      misses: this.stats.misses,
      sets: this.stats.sets,
      evictions: this.stats.evictions,
      compressions: this.stats.compressions
    };
  }

  /**
   * Get detailed cache information
   * @returns {object} Detailed cache information
   */
  getDetailedInfo() {
    const items = Array.from(this.cacheMetadata.entries()).map(([key, metadata]) => ({
      key,
      size: metadata.size,
      createdAt: metadata.createdAt,
      expiresAt: metadata.expiresAt,
      lastAccessedAt: metadata.lastAccessedAt,
      accessCount: metadata.accessCount,
      ttl: metadata.ttl,
      tags: metadata.tags,
      priority: metadata.priority,
      timeToExpiry: metadata.expiresAt - Date.now(),
      compressed: this.cache.get(key)?.compressed || false
    }));

    // Sort by last accessed (most recent first)
    items.sort((a, b) => b.lastAccessedAt - a.lastAccessedAt);

    return {
      items,
      statistics: this.getStatistics(),
      config: this.config
    };
  }

  /**
   * Create cache-busting parameters for requests
   * @param {object} options - Cache busting options
   * @returns {object} Cache busting parameters
   */
  createCacheBustingParams(options = {}) {
    const {
      includeTimestamp = true,
      includeRandom = false,
      customParams = {}
    } = options;

    const params = { ...customParams };

    if (includeTimestamp) {
      params._t = Date.now();
    }

    if (includeRandom) {
      params._r = Math.random().toString(36).substr(2, 9);
    }

    return params;
  }

  /**
   * Wrap a function with caching
   * @param {Function} fn - Function to wrap
   * @param {object} options - Caching options
   * @returns {Function} Wrapped function
   */
  wrapWithCache(fn, options = {}) {
    const {
      keyGenerator = (...args) => JSON.stringify(args),
      ttl = this.config.defaultTtl,
      tags = [],
      priority = 'normal'
    } = options;

    return async (...args) => {
      const key = keyGenerator(...args);
      
      // Try to get from cache first
      const cached = this.get(key);
      if (cached !== null) {
        return cached;
      }

      // Execute function and cache result
      try {
        const result = await fn(...args);
        this.set(key, result, { ttl, tags, priority });
        return result;
      } catch (error) {
        // Don't cache errors
        throw error;
      }
    };
  }

  /**
   * Start periodic cleanup of expired items
   */
  startCleanup() {
    setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Clean up expired items
   */
  cleanup() {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [key, metadata] of this.cacheMetadata.entries()) {
      if (now > metadata.expiresAt) {
        this.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(`[CacheManager] Cleaned up ${cleanedCount} expired items`);
    }
  }

  /**
   * Export cache data for debugging
   * @returns {object} Exportable cache data
   */
  exportData() {
    return {
      statistics: this.getStatistics(),
      items: this.getDetailedInfo().items,
      config: this.config,
      exportedAt: new Date().toISOString()
    };
  }
}

// Create singleton instance
const cacheManager = new CacheManager();

export default cacheManager;
